import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { FetchAssetParams } from '../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { LinkAddressParams } from '../../fulfillmentContext/types';
import type { PartnerAddressUpsert, PartnerAddressV2 } from '@shipium-corp/shipium-ts-types';
import type { StoreModel } from '../..';

export interface AddressModel {
  fetchAddresses: Thunk<AddressModel, FetchAssetParams, Injections, Promise<PartnerAddressV2[]>>;
  fetchAddress: Thunk<AddressModel, string, Injections, Promise<PartnerAddressV2>>;
  createAddress: Thunk<AddressModel, PartnerAddressUpsert, Injections, StoreModel, Promise<PartnerAddressV2>>;
  linkAddress: Thunk<AddressModel, LinkAddressParams, Injections, StoreModel, Promise<PartnerAddressV2>>;
  addressActionStart: Action<AddressModel, void>;
  lastAction?: number;
  addresses: Record<string, PartnerAddressV2>;
  addressFailure: Action<AddressModel, ApplicationErrorOptions>;
  setAddresses: Action<AddressModel, PartnerAddressV2[]>;
}

export const addressModel: AddressModel = {
  addresses: {},
  addressFailure: action((state, payload) => {
    console.error(payload);
  }),
  setAddresses: action((state, payload) => {
    for (const val of payload) {
      state.addresses[val.addressId!] = val;
    }
  }),
  addressActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  fetchAddresses: thunk(async (actions, payload, { injections }) => {
    try {
      actions.addressActionStart();
      const res = await injections.assetService.fetchAddresses(payload);
      // SPULEO 2024-07-23: Disabling this fetch because of performance issues with partners with 100+ addresses.
      // Unclear where / if this is used.  Keeping the interface intact for now.
      // const zip3s = await Promise.allSettled(res.map((val) => injections.fulfillmentConfigService.fetchZip3Distances({ zip3: val.location.postalCode.substring(0, 3) })));
      // const addressesWithZips = res.map((val, idx) => (
      //   zip3s[idx].status==='fulfilled' ?
      //   {
      //     ...val,
      //     zip3Distances:(zip3s[idx] as PromiseFulfilledResult<any>)?.value
      //   }:{...val}))
      const addressesWithZips = res.map((val) => ({ ...val, zip3Distances: [] }));
      actions.setAddresses(addressesWithZips);
      return res;
    } catch (error) {
      actions.addressFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching addresses',
        asNotification: true,
      });
    }
  }),
  fetchAddress: thunk(async (actions, payload, { injections }) => {
    try {
      actions.addressActionStart();
      const res = await injections.assetService.fetchAddress(payload);
      actions.setAddresses([res]);
      return res;
    } catch (error) {
      actions.addressFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching address',
        asNotification: true,
      });
    }
  }),
  createAddress: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.addressActionStart();
      const res = await injections.assetService.createAddress(payload);
      actions.setAddresses([res]);

      // Invalidate address queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-create',
        entityId: res.addressId,
      });

      return res;
    } catch (error) {
      actions.addressFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating address',
        asNotification: true,
      });
      return error;
    }
  }),
  linkAddress: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.addressActionStart();
      const res = await injections.assetService.linkAddress(payload);

      // Invalidate address queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'address-link',
        entityId: payload.addressId,
      });

      return res;
    } catch (error) {
      actions.addressFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured linking address',
        asNotification: true,
      });
      return error;
    }
  }),
};
